import { useEffect, useState } from 'react';
import { Box, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';
import { isPwaMode } from '../../utils/pwaStatusUtils';

// 定义动画
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

interface SplashScreenProps {
  minDisplayTime?: number; // 最小显示时间（毫秒）
  fadeOutTime?: number; // 淡出时间（毫秒）
  onFinish?: () => void; // 结束回调
}

export function SplashScreen({
  minDisplayTime = 2000,
  fadeOutTime = 500,
  onFinish
}: SplashScreenProps) {
  const { t } = useTranslation(['v0/common']);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'gray.100');

  // 检查是否是 PWA 模式
  const isPwa = isPwaMode();

  useEffect(() => {
    // 如果不是 PWA 模式，不显示 Splash Screen
    if (!isPwa) {
      setIsVisible(false);
      onFinish?.();
      return;
    }

    // 记录开始时间
    const startTime = Date.now();

    // 标记内容已加载
    const markAsLoaded = () => {
      setIsLoaded(true);

      // 计算还需要显示的时间
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      // 设置定时器，在最小显示时间后开始淡出
      setTimeout(() => {
        setIsFadingOut(true);

        // 淡出后隐藏 Splash Screen
        setTimeout(() => {
          setIsVisible(false);
          onFinish?.();
        }, fadeOutTime);
      }, remainingTime);
    };

    // 监听页面加载完成事件
    if (document.readyState === 'complete') {
      markAsLoaded();
    } else {
      window.addEventListener('load', markAsLoaded);
      return () => window.removeEventListener('load', markAsLoaded);
    }
  }, [minDisplayTime, fadeOutTime, onFinish, isPwa]);

  // 如果不可见，不渲染任何内容
  if (!isVisible) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex="9999"
      bg={bgColor}
      animation={isFadingOut ? `${fadeOut} ${fadeOutTime}ms ease-out forwards` : `${fadeIn} 300ms ease-in`}
    >
      <Flex
        height="100%"
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Box
          animation={`${pulse} 2s infinite ease-in-out`}
          mb={6}
        >
          <Image
            src="/icons/icon-512.webp"
            alt="Tempo"
            width="120px"
            height="120px"
            borderRadius="24px"
            boxShadow="0 4px 8px rgba(0, 0, 0, 0.1)"
          />
        </Box>

        <Text
          fontSize="2xl"
          fontWeight="bold"
          color={textColor}
          mb={2}
        >
          {t('v0/common:splashScreen.appName')}
        </Text>

        <Text
          fontSize="md"
          color={textColor}
          opacity="0.8"
        >
          {t('v0/common:splashScreen.appDescription')}
        </Text>

        <Box
          mt={8}
          width="40px"
          height="4px"
          borderRadius="full"
          bg="teal.500"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            height="100%"
            width="100%"
            bg="teal.300"
            animation={`${pulse} 1.5s infinite ease-in-out`}
          />
        </Box>
      </Flex>
    </Box>
  );
}
